import { useState } from "react";
import { Check, X, AlertCircle, Ban, RotateCcw, Sparkles, Eye, FileText, Loader2, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/Quote";
import { DynamicFieldsDisplay, DynamicFieldsForm } from "@/components/DynamicFields";
import FileUpload from "@/components/FileUpload";
import api, { fileUrl } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";

export default function VendorsTab({ vendors, reload, queueOnly = false }) {
  const [detail, setDetail] = useState(null); const [note, setNote] = useState(""); const [q, setQ] = useState(""); const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [editFields, setEditFields] = useState([]);
  const [planOpen, setPlanOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [planBusy, setPlanBusy] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [messageBusy, setMessageBusy] = useState(false);
  const [pauseBusy, setPauseBusy] = useState(false);
  const list = (queueOnly ? vendors.filter((v) => ["submitted", "under_review", "corrections_requested"].includes(v.status)) : vendors).filter((v) => !q || v.business_name.toLowerCase().includes(q.toLowerCase()));

  const act = async (v, action) => {
    if (["reject", "request_corrections", "suspend"].includes(action) && !note) { toast.error("Add a note for the vendor"); return; }
    setBusy(true);
    try { await api.post(`/admin/vendors/${v.id}/${action}`, { note: note || null }); toast.success(`Vendor: ${action.replace("_", " ")}`); setDetail(null); setNote(""); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const open = async (v) => { const { data } = await api.get(`/admin/vendors/${v.id}`); setDetail(data); };
  const d = detail?.vendor;

  const openEditVendor = () => {
    const v = d || {};
    setEditData({
      ...v,
      social: { ...(v.social || {}) },
      bank: { ...(v.bank || {}) },
      subcategories: Array.isArray(v.subcategories) ? v.subcategories : [],
      amenities: Array.isArray(v.amenities) ? v.amenities : [],
      service_areas: Array.isArray(v.service_areas) ? v.service_areas : [],
      kyc_documents: Array.isArray(v.kyc_documents) ? v.kyc_documents : [],
      gallery: Array.isArray(v.gallery) ? v.gallery : [],
      videos: Array.isArray(v.videos) ? v.videos : [],
      custom_fields: { ...(v.custom_fields || {}) },
      starting_price: v.starting_price ?? "",
      years: v.years ?? "",
      service_radius_km: v.service_radius_km ?? "",
      geo: v.geo?.coordinates
        ? { lat: v.geo.coordinates[1], lng: v.geo.coordinates[0] }
        : (v.geo || {}),
    });
    setEditFields(Array.isArray(detail?.fields) ? detail.fields : []);
    setEditOpen(true);
  };

  const openWhatsApp = () => {
    const phone = String(d?.whatsapp_number || detail?.owner?.phone || d?.business_phone || "").replace(/\D/g, "");
    if (!phone) {
      toast.error("Vendor mobile number not available");
      return;
    }

    const number = phone.length === 10 ? `91${phone}` : phone;
    const message = encodeURIComponent(
      `Hello ${d?.business_name || "Vendor"}, this is MakeMyEventPro Super Admin.`
    );

    window.open(`https://wa.me/${number}?text=${message}`, "_blank", "noopener,noreferrer");
  };

  const saveEditVendor = async () => {
    if (!d?.id) return;
    setBusy(true);
    try {
      const payload = {
        ...editData,
        starting_price: editData.starting_price === "" ? null : Number(editData.starting_price),
        years: editData.years === "" || editData.years == null ? null : Number(editData.years),
        service_radius_km: editData.service_radius_km === "" || editData.service_radius_km == null
          ? null : Number(editData.service_radius_km),
      };
      if (payload.geo && payload.geo.lat !== "" && payload.geo.lng !== ""
          && payload.geo.lat != null && payload.geo.lng != null) {
        payload.geo = { lat: Number(payload.geo.lat), lng: Number(payload.geo.lng) };
      } else {
        payload.geo = null;
      }
      await api.put(`/admin/vendors/${d.id}/edit`, payload);
      toast.success("Vendor details updated");
      setEditOpen(false);
      setDetail(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update vendor");
    } finally {
      setBusy(false);
    }
  };

  const toggleVendorServices = async () => {
    if (!d?.id) return;

    const isPaused = d?.services_paused === true;

    if (!isPaused) {
      const confirmed = window.confirm(
        `Pause services for ${d?.business_name || "this vendor"}?\n\nThe vendor will remain approved, but customers will not see the vendor in marketplace listings until services are resumed.`
      );
      if (!confirmed) return;
    }

    setPauseBusy(true);
    try {
      await api.post(`/admin/vendors/${d.id}/${isPaused ? "resume" : "pause"}`, {
        note: isPaused ? null : "Services paused by Super Admin",
      });

      toast.success(isPaused ? "Vendor services resumed" : "Vendor services paused");

      const { data } = await api.get(`/admin/vendors/${d.id}`);
      setDetail(data);
      reload();
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
        (isPaused ? "Failed to resume vendor services" : "Failed to pause vendor services")
      );
    } finally {
      setPauseBusy(false);
    }
  };

  const openChangePlan = async () => {
    if (!d?.id) return;

    setPlanBusy(true);
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(Array.isArray(data) ? data : []);
      setSelectedPlan(d?.subscription?.plan_slug || "");
      setPlanOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load vendor plans");
    } finally {
      setPlanBusy(false);
    }
  };

  const saveChangePlan = async () => {
    if (!d?.id || !selectedPlan) {
      toast.error("Select a plan");
      return;
    }

    setPlanBusy(true);
    try {
      await api.post(`/admin/vendors/${d.id}/plan`, {
        plan_slug: selectedPlan,
      });

      toast.success("Vendor plan updated");
      setPlanOpen(false);
      setDetail(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change vendor plan");
    } finally {
      setPlanBusy(false);
    }
  };

  const deleteVendor = async () => {
    if (!d?.id) return;

    const confirmed = window.confirm(
      `Delete "${d.business_name || "this vendor"}"?\n\nThis will permanently remove the vendor account and vendor profile. This action cannot be undone.`
    );

    if (!confirmed) return;

    setBusy(true);
    try {
      await api.delete(`/admin/vendors/${d.id}`);
      toast.success("Vendor deleted successfully");
      setDetail(null);
      setNote("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete vendor");
    } finally {
      setBusy(false);
    }
  };

  const sendVendorMessage = async () => {
    const message = messageText.trim();

    if (!d?.id) return;
    if (!message) {
      toast.error("Enter a message");
      return;
    }

    setMessageBusy(true);
    try {
      await api.post(`/admin/vendors/${d.id}/message`, { message });
      toast.success("Message sent to vendor");
      setMessageText("");
      setMessageOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send message");
    } finally {
      setMessageBusy(false);
    }
  };

  return (
    <div data-testid={queueOnly ? "kyc-queue" : "admin-vendors-table"}>
      {!queueOnly && <Input data-testid="vendor-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors…" className="rounded-xl max-w-xs mb-4" />}
      {list.length === 0 ? <p className="text-muted-foreground py-8 text-center">{queueOnly ? "No vendors awaiting verification." : "No vendors."}</p> : (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Vendor ID</TableHead><TableHead>Business</TableHead><TableHead>Added By</TableHead><TableHead>Category</TableHead><TableHead>Area</TableHead><TableHead>Plan</TableHead><TableHead>Registered</TableHead><TableHead>Status</TableHead><TableHead>Flags</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>{list.map((v) => (
            <TableRow key={v.id} data-testid={`vendor-row-${v.id}`}>
              <TableCell className="font-mono text-xs font-semibold">{v.vendor_code || "—"}</TableCell>
                <TableCell className="font-medium">{v.business_name}<div className="text-xs text-muted-foreground">{v.owner_name} · {v.business_phone}</div></TableCell>
                <TableCell>
                  {v.added_by_employee_id ? (
                    <div className="text-sm font-medium">{v.added_by_employee_name || "Employee name unavailable"}
                      <div className="text-xs text-muted-foreground">{v.added_by_employee_id}</div>
                    </div>
                  ) : <span className="text-xs text-muted-foreground">Not recorded</span>}
                </TableCell>
                <TableCell>{v.category_name}</TableCell>
                <TableCell>{v.area}</TableCell>
                <TableCell>
                  <div className="font-medium">{v.subscription?.plan_name || "No plan"}</div>
                  <div className="text-xs text-muted-foreground">{v.subscription?.status || "—"}</div>
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">{v.created_at ? new Date(v.created_at).toLocaleString("en-IN") : "—"}</TableCell>
                <TableCell><StatusBadge status={v.status} /></TableCell>
                <TableCell className="text-xs">{v.featured && <Sparkles className="inline h-4 w-4 text-amber-500" />} {v.premium && "PREMIUM"}</TableCell>
              <TableCell><div className="flex gap-1 flex-wrap">
                <Button data-testid={`review-${v.id}`} size="sm" variant="outline" className="rounded-lg h-8" onClick={() => open(v)}><Eye className="h-3.5 w-3.5 mr-1" /> Review</Button>
                {["submitted", "under_review"].includes(v.status) && <Button data-testid={`approve-${v.id}`} size="sm" className="rounded-lg h-8 bg-emerald-500 hover:bg-emerald-600" onClick={() => act(v, "approve")}><Check className="h-3.5 w-3.5" /></Button>}
                {v.status === "approved" && <Button size="sm" variant="outline" className="rounded-lg h-8 text-xs" onClick={() => act(v, v.featured ? "unfeature" : "feature")}>{v.featured ? "Unfeature" : "Feature"}</Button>}
                {v.status === "approved" && <Button size="sm" variant="outline" className="rounded-lg h-8 text-xs" onClick={() => act(v, v.premium ? "unpremium" : "premium")}>{v.premium ? "Unpremium" : "Premium"}</Button>}
              </div></TableCell>
            </TableRow>))}</TableBody></Table></div>)}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
  <DialogContent
    className="p-0 bg-white dark:bg-slate-900 rounded-3xl max-w-6xl w-[96vw] max-h-[94vh] overflow-hidden"
    data-testid="vendor-review-dialog"
  >
    {d && (
      <div className="flex flex-col max-h-[94vh]">

        {/* PREMIUM VENDOR HEADER */}
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-slate-50 via-purple-50/70 to-blue-50/70 dark:from-slate-900 dark:via-purple-950/30 dark:to-blue-950/30 border-b">
          <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-purple-200/30 blur-3xl" />
          <div className="absolute right-20 -bottom-28 h-64 w-64 rounded-full bg-blue-200/30 blur-3xl" />

          <div className="relative p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-5">

              {/* Vendor image */}
              <div className="shrink-0">
                {d.gallery?.length > 0 ? (
                  <img
                    src={fileUrl(d.gallery[0])}
                    alt={d.business_name || "Vendor"}
                    className="h-28 w-28 sm:h-36 sm:w-36 rounded-2xl object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="h-28 w-28 sm:h-36 sm:w-36 rounded-2xl bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center border-4 border-white shadow-lg">
                    <Sparkles className="h-10 w-10 text-purple-500" />
                  </div>
                )}
              </div>

              {/* Vendor identity */}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Vendor Details
                </div>

                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 min-w-0">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white leading-tight break-words whitespace-normal">
                      {d.business_name || "Unnamed Vendor"}
                    </h2>

                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                      {d.whatsapp_number && (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-emerald-600">●</span>
                          WhatsApp
                        </span>
                      )}

                      {d.social && (
                        <>
                          <span>•</span>
                          <span>Social Media</span>
                        </>
                      )}
                    </div>

                    <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                      📍 {d.city || d.address || "Location not specified"}
                      {d.category_name ? ` • ${d.category_name}` : ""}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {d.vegetarian === true && (
                        <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                          ✓ Vegetarian
                        </span>
                      )}

                      {d.non_vegetarian === true && (
                        <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                          ✓ Non-vegetarian
                        </span>
                      )}

                      {d.vendor_code && (
                        <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold">
                          {d.vendor_code}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0 self-start">
                    <StatusBadge status={d.status} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TOP ACTION BAR */}
        <div className="shrink-0 px-5 sm:px-6 py-4 bg-white dark:bg-slate-900 border-b">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">

            <Button
              variant="outline"
              className="h-14 rounded-2xl border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-700 hover:from-blue-100 hover:to-indigo-100 shadow-sm"
              onClick={openEditVendor}
            >
              <Pencil className="h-5 w-5 mr-2" />
              <span className="font-semibold">Edit Vendor Details</span>
            </Button>

            <Button
              variant="outline"
              className="h-14 rounded-2xl border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 text-emerald-700 hover:from-emerald-100 hover:to-green-100 shadow-sm"
              onClick={openWhatsApp}
            >
              <span className="text-lg mr-2">●</span>
              <span className="font-semibold">WhatsApp</span>
            </Button>

            <Button
              variant="outline"
              className="h-14 rounded-2xl border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 text-amber-700 hover:from-amber-100 hover:to-orange-100 shadow-sm"
              onClick={openChangePlan}
              disabled={planBusy}
            >
              <Sparkles className="h-5 w-5 mr-2" />
              <span className="font-semibold">Change Plan</span>
            </Button>

            <Button
              variant="outline"
              className="h-14 rounded-2xl border-sky-200 bg-gradient-to-br from-sky-50 to-blue-50 text-sky-700 hover:from-sky-100 hover:to-blue-100 shadow-sm"
              onClick={() => setMessageOpen(true)}
            >
              <span className="text-lg mr-2">💬</span>
              <span className="font-semibold">Send Message</span>
            </Button>

            <Button
              variant="outline"
              className={`h-14 rounded-2xl shadow-sm transition-all ${
                d.services_paused
                  ? "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 text-sky-700 hover:from-sky-100 hover:to-cyan-100"
                  : "border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50 text-rose-700 hover:from-rose-100 hover:to-pink-100"
              }`}
              onClick={toggleVendorServices}
              disabled={pauseBusy}
            >
              <span className="text-lg mr-2">{d.services_paused ? "▶" : "Ⅱ"}</span>
              <span className="font-semibold">
                {pauseBusy ? "Updating..." : d.services_paused ? "Resume Services" : "Pause Services"}
              </span>
            </Button>

          </div>
        </div>

        {/* SCROLLABLE CONTENT */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-5 space-y-6">

          {/* BUSINESS INFORMATION */}
          <section>
            <div className="text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-3">
              Business Information
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Vendor ID</div>
                <div className="font-semibold mt-1">{d.vendor_code || d.id || "—"}</div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Registered</div>
                <div className="font-semibold mt-1">
                  {d.created_at ? new Date(d.created_at).toLocaleString("en-IN") : "—"}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Current Plan</div>
                <div className="font-semibold mt-1">
                  {d.subscription?.plan_name || d.plan_name || "—"}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Plan Price</div>
                <div className="font-semibold mt-1">
                  {d.subscription?.price != null
                    ? formatINR(d.subscription.price)
                    : d.plan_price != null
                    ? formatINR(d.plan_price)
                    : "—"}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Subscription Status</div>
                <div className="font-semibold mt-1">
                  {d.subscription?.status || d.subscription_status || "—"}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Valid Until</div>
                <div className="font-semibold mt-1">
                  {d.subscription?.valid_until
                    ? new Date(d.subscription.valid_until).toLocaleDateString("en-IN")
                    : d.subscription_valid_until
                    ? new Date(d.subscription_valid_until).toLocaleDateString("en-IN")
                    : "—"}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Owner</div>
                <div className="font-semibold mt-1">{detail?.owner?.name || "—"}</div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Business Type</div>
                <div className="font-semibold mt-1">{d.business_type || "—"}</div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Category</div>
                <div className="font-semibold mt-1">
                  {d.category_name || d.category_slug || "—"}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">WhatsApp</div>
                <div className="font-semibold mt-1">{d.whatsapp_number || "—"}</div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Business Phone</div>
                <div className="font-semibold mt-1">{d.business_phone || detail?.owner?.phone || "—"}</div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Email</div>
                <div className="font-semibold mt-1 break-all">{detail?.owner?.email || d.email || "—"}</div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Website</div>
                <div className="font-semibold mt-1 break-all">{d.website || "—"}</div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">GST</div>
                <div className="font-semibold mt-1">{d.gst || d.gst_number || "—"}</div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">PAN</div>
                <div className="font-semibold mt-1">{d.pan || d.pan_number || "—"}</div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Registration</div>
                <div className="font-semibold mt-1">{d.registration || d.registration_number || "—"}</div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Starting Price</div>
                <div className="font-semibold mt-1">
                  {d.starting_price != null ? formatINR(d.starting_price) : "—"}
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-slate-500">Service Areas</div>
                <div className="font-semibold mt-1">
                  {Array.isArray(d.service_areas)
                    ? d.service_areas.join(", ")
                    : d.service_areas || "—"}
                </div>
              </div>

            </div>
          </section>

          {/* ADDRESS */}
          <section>
            <div className="text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-3">
              Address
            </div>
            <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
              <div className="font-semibold">{d.address || "—"}</div>
              {(d.city || d.state || d.pincode) && (
                <div className="text-sm text-slate-500 mt-1">
                  {[d.city, d.state, d.pincode].filter(Boolean).join(", ")}
                </div>
              )}
            </div>
          </section>

          {/* DESCRIPTION */}
          <section>
            <div className="text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-3">
              Description
            </div>
            <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
              {d.description || "—"}
            </div>
          </section>

          {/* SOCIAL */}
          {d.social && (
            <section>
              <div className="text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-3">
                Social
              </div>
              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 p-4">
                {typeof d.social === "object"
                  ? Object.entries(d.social).map(([key, value]) => (
                      <div key={key} className="mb-1">
                        <span className="font-medium capitalize">{key}:</span>{" "}
                        <span>{String(value)}</span>
                      </div>
                    ))
                  : d.social}
              </div>
            </section>
          )}

          {/* KYC */}

          {/* KYC */}
          <section>
            <div className="text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-3">
              KYC Documents
            </div>

            <div className="flex flex-wrap gap-3">
              {(d.kyc_documents || []).map((k, i) =>
                k.file_id ? (
                  <a
                    key={i}
                    href={fileUrl(k.file_id, true)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border bg-white dark:bg-slate-800 hover:border-purple-300 hover:shadow-sm transition"
                    data-testid={`kyc-doc-${i}`}
                  >
                    <FileText className="h-5 w-5 text-purple-500" />
                    <span className="font-medium">{k.type}</span>
                  </a>
                ) : (
                  <span key={i} className="px-4 py-2.5 rounded-xl bg-muted text-sm">
                    {k.type} (missing file)
                  </span>
                )
              )}

              {!d.kyc_documents?.length && (
                <span className="text-sm text-muted-foreground">None uploaded</span>
              )}
            </div>

            {d.gallery?.length > 0 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                {d.gallery.slice(0, 8).map((g, i) => (
                  <img
                    key={i}
                    src={fileUrl(g)}
                    alt=""
                    className="h-24 w-24 rounded-2xl object-cover shrink-0 border shadow-sm"
                  />
                ))}
              </div>
            )}
          </section>

          {/* DYNAMIC VENDOR INFORMATION */}
          {detail.fields?.length > 0 && (
            <section>
              <DynamicFieldsDisplay
                fields={detail.fields}
                values={d.custom_fields || {}}
              />
            </section>
          )}

          {/* BASIC INFORMATION */}
          {/* ADMIN NOTE */}
          <section>
            <Textarea
              data-testid="admin-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note to vendor (required for reject / corrections / suspend)"
              className="rounded-2xl min-h-[100px]"
            />
          </section>

          {/* REVIEW ACTIONS */}
          <section className="rounded-2xl border bg-gradient-to-br from-slate-50 to-purple-50/40 dark:from-slate-800 dark:to-purple-950/20 p-4">
            <div className="text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-3">
              Review & Status
            </div>

            <div className="flex flex-wrap gap-2">
              {["submitted"].includes(d.status) && (
                <Button
                  data-testid="act-review"
                  variant="outline"
                  className="rounded-xl"
                  disabled={busy}
                  onClick={() => act(d, "review")}
                >
                  Mark under review
                </Button>
              )}

              {["submitted", "under_review"].includes(d.status) && (
                <Button
                  data-testid="act-approve"
                  className="rounded-xl bg-emerald-500 hover:bg-emerald-600"
                  disabled={busy}
                  onClick={() => act(d, "approve")}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
              )}

              {["submitted", "under_review", "corrections_requested", "rejected"].includes(d.status) && (
                <Button
                  data-testid="act-corrections"
                  variant="outline"
                  className="rounded-xl text-orange-600 border-orange-200"
                  disabled={busy}
                  onClick={() => act(d, "request_corrections")}
                >
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Request corrections
                </Button>
              )}

              {["submitted", "under_review"].includes(d.status) && (
                <Button
                  data-testid="act-reject"
                  variant="outline"
                  className="rounded-xl text-red-600 border-red-200"
                  disabled={busy}
                  onClick={() => act(d, "reject")}
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              )}

              {d.status === "approved" && (
                <Button
                  data-testid="act-suspend"
                  variant="outline"
                  className="rounded-xl text-red-600 border-red-200"
                  disabled={busy}
                  onClick={() => act(d, "suspend")}
                >
                  <Ban className="h-4 w-4 mr-1" />
                  Suspend
                </Button>
              )}

              {d.status === "suspended" && (
                <Button
                  data-testid="act-reactivate"
                  variant="outline"
                  className="rounded-xl"
                  disabled={busy}
                  onClick={() => act(d, "reactivate")}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reactivate
                </Button>
              )}

              <Button
        type="button"
        variant="outline"
        data-testid="act-delete-vendor"
        className="rounded-xl text-red-700 border-red-300 hover:bg-red-50"
        disabled={busy}
        onClick={deleteVendor}
      >
        <Trash2 className="h-4 w-4 mr-1" />
        Delete Vendor
      </Button>

      {busy && <Loader2 className="h-5 w-5 animate-spin self-center" />}
            </div>
          </section>

        </div>
      </div>
    )}
  </DialogContent>
</Dialog>

<Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 rounded-3xl max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Vendor Details</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <section className="space-y-3">
              <h3 className="font-semibold">Business and contact details</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  ["Business Name", "business_name"],
                  ["Owner / Contact Person", "owner_name"],
                  ["Business Type", "business_type"],
                  ["Years in Business", "years", "number"],
                  ["Business Phone", "business_phone"],
                  ["WhatsApp Number", "whatsapp_number"],
                  ["Email", "email"],
                  ["Website", "website"],
                  ["Category Slug", "category_slug"],
                  ["Category Name", "category_name"],
                  ["City", "city"],
                  ["Area / Locality", "area"],
                  ["Pincode", "pincode"],
                  ["Starting Price (₹)", "starting_price", "number"],
                  ["Price Unit", "price_unit"],
                  ["Service Radius (km)", "service_radius_km", "number"],
                  ["GST Number", "gst_number"],
                  ["PAN Number", "pan_number"],
                  ["Registration Type", "registration_type"],
                  ["Registration Number", "registration_number"],
                ].map(([label, key, type]) => (
                  <div key={key} className="space-y-1">
                    <label className="text-sm font-medium">{label}</label>
                    <Input
                      type={type || "text"}
                      value={editData[key] ?? ""}
                      onChange={(e) => setEditData((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                  </div>
                ))}
                {[
                  ["Address", "address"],
                  ["Business Description", "description"],
                ].map(([label, key]) => (
                  <div key={key} className="sm:col-span-2 space-y-1">
                    <label className="text-sm font-medium">{label}</label>
                    <Textarea
                      rows={key === "description" ? 5 : 3}
                      value={editData[key] || ""}
                      onChange={(e) => setEditData((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold">Categories and service areas</h3>
              <p className="text-xs text-muted-foreground">For list fields, separate each value with a comma.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  ["Subcategories", "subcategories"],
                  ["Amenities / Highlights", "amenities"],
                  ["Service Areas", "service_areas"],
                ].map(([label, key]) => (
                  <div key={key} className="space-y-1">
                    <label className="text-sm font-medium">{label}</label>
                    <Input
                      value={(editData[key] || []).join(", ")}
                      onChange={(e) => setEditData((prev) => ({
                        ...prev,
                        [key]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
                      }))}
                    />
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold">Social profiles</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {["whatsapp", "instagram", "facebook", "youtube", "twitter", "linkedin", "justdial"].map((key) => (
                  <div key={key} className="space-y-1">
                    <label className="text-sm font-medium">{key === "whatsapp" ? "WhatsApp" : key.charAt(0).toUpperCase() + key.slice(1)}</label>
                    <Input
                      value={editData.social?.[key] || ""}
                      onChange={(e) => setEditData((prev) => ({
                        ...prev, social: { ...(prev.social || {}), [key]: e.target.value },
                      }))}
                    />
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold">Map location</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {["lat", "lng"].map((key) => (
                  <div key={key} className="space-y-1">
                    <label className="text-sm font-medium">{key === "lat" ? "Latitude" : "Longitude"}</label>
                    <Input
                      type="number"
                      value={editData.geo?.[key] ?? ""}
                      onChange={(e) => setEditData((prev) => ({
                        ...prev, geo: { ...(prev.geo || {}), [key]: e.target.value },
                      }))}
                    />
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold">Business media</h3>
              <FileUpload label="Business logo" value={editData.logo ? [editData.logo] : []}
                onChange={(ids) => setEditData((prev) => ({ ...prev, logo: ids[0] || null }))}
                kind="image" purpose="logo" max={1} />
              <FileUpload label="Cover photo" value={editData.cover ? [editData.cover] : []}
                onChange={(ids) => setEditData((prev) => ({ ...prev, cover: ids[0] || null }))}
                kind="image" purpose="cover" max={1} />
              <FileUpload label="Business photos (up to 15)" value={editData.gallery || []}
                onChange={(ids) => setEditData((prev) => ({ ...prev, gallery: ids }))}
                kind="image" purpose="gallery" max={15} />
              <FileUpload label="Business videos (up to 3)" value={editData.videos || []}
                onChange={(ids) => setEditData((prev) => ({ ...prev, videos: ids }))}
                kind="video" purpose="video" max={3} />
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold">KYC documents</h3>
              {(editData.kyc_documents || []).map((doc, i) => (
                <div key={i} className="rounded-xl border p-3 space-y-2">
                  <div className="flex gap-2 items-center">
                    <Input
                      value={doc.type || ""}
                      placeholder="Document type"
                      onChange={(e) => setEditData((prev) => ({
                        ...prev,
                        kyc_documents: prev.kyc_documents.map((x, j) => j === i ? { ...x, type: e.target.value } : x),
                      }))}
                    />
                    <Button type="button" variant="outline" onClick={() => setEditData((prev) => ({
                      ...prev, kyc_documents: prev.kyc_documents.filter((_, j) => j !== i),
                    }))}>Remove</Button>
                  </div>
                  <FileUpload label="Document file" value={doc.file_id ? [doc.file_id] : []}
                    onChange={(ids) => setEditData((prev) => ({
                      ...prev,
                      kyc_documents: prev.kyc_documents.map((x, j) => j === i ? { ...x, file_id: ids[0] || null } : x),
                    }))}
                    kind="document" purpose="kyc" max={1} priv />
                </div>
              ))}
              <Button type="button" variant="outline" onClick={() => setEditData((prev) => ({
                ...prev, kyc_documents: [...(prev.kyc_documents || []), { type: "Business Proof", file_id: null }],
              }))}>+ Add KYC document</Button>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold">Category-specific fields</h3>
              {editFields.length ? (
                <DynamicFieldsForm
                  fields={editFields}
                  values={editData.custom_fields || {}}
                  onChange={(values) => setEditData((prev) => ({ ...prev, custom_fields: values }))}
                />
              ) : (
                <p className="text-sm text-muted-foreground">No category-specific fields are configured for this vendor.</p>
              )}
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold">Registration declarations</h3>
              {[
                ["Terms accepted", "terms_accepted"],
                ["Declaration accepted", "declaration_accepted"],
              ].map(([label, key]) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editData[key]}
                    onChange={(e) => setEditData((prev) => ({ ...prev, [key]: e.target.checked }))} />
                  {label}
                </label>
              ))}
            </section>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setEditOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="rounded-xl bg-purple-600 hover:bg-purple-700"
              onClick={saveEditVendor}
              disabled={busy}
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change Vendor Plan</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {plans.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                No active vendor plans available.
              </div>
            ) : (
              plans.filter((p) => p.active !== false).map((plan) => (
                <button
                  key={plan.slug || plan.id}
                  type="button"
                  onClick={() => setSelectedPlan(plan.slug || plan.id)}
                  className={`w-full text-left rounded-2xl border p-4 transition ${
                    selectedPlan === (plan.slug || plan.id)
                      ? "border-purple-500 bg-purple-50 dark:bg-purple-950/30"
                      : "border-border hover:border-purple-300"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold">{plan.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatINR(plan.price_monthly)} / month
                      </div>
                    </div>
                    {selectedPlan === plan.id && (
                      <Check className="h-5 w-5 text-purple-600" />
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground mt-2">
                    Validity: {plan.validity_days || 30} days
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setPlanOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="rounded-xl bg-purple-600 hover:bg-purple-700"
              onClick={saveChangePlan}
              disabled={planBusy || !selectedPlan}
            >
              {planBusy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Change Plan
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle>Message Vendor</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Send a message to <span className="font-semibold text-foreground">{d?.business_name || "vendor"}</span>.
            </div>

            <Textarea
              rows={6}
              placeholder="Type your message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              disabled={messageBusy}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setMessageOpen(false)}
              disabled={messageBusy}
            >
              Cancel
            </Button>
            <Button
              className="rounded-xl bg-purple-600 hover:bg-purple-700"
              onClick={sendVendorMessage}
              disabled={messageBusy || !messageText.trim()}
            >
              {messageBusy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Send Message
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
const Info = ({ label, v }) => <div><div className="text-xs text-muted-foreground">{label}</div><div className="font-medium break-words">{v}</div></div>;
